import React from "react";
import FormLabel from "../formLabel";
import FormGroup from "../formGroup";
import ErrorField from "../errorField";
import { useField, useFormikContext } from "formik";
import { FieldResetWhenRemoved, getValueForReadOnlyFieldForStaticForm } from "shared/form/util";
import { ApiFieldAsset } from "shared/types/ApiFieldAsset";
import AssetUpload from "shared/form/core/assetUpload";
import { FormAssetUploadFieldConfig } from "./type";
import { AssetInputMode, AssetUploadType } from "../../core/assetUpload/types";
import { AssetType } from "../../../enums/AssetType";
import FieldRenderType from "shared/form/renderer/enums/FieldRenderType";
import { useFormContextData } from "shared/form/context";
import isEmpty from "lodash/isEmpty";
import FormOverwriteField from "../overwriteField";
import { ConflictType } from "shared/form/renderer/enums/Conflict";

function FormAssetUploadField({
  assetUploadType = AssetUploadType.IMAGE,
  assetType = AssetType.USER_INPUT,
  ...props
}: FormAssetUploadFieldConfig) {
  const formContextData = useFormContextData();
  const { setFieldTouched, setFieldValue } = useFormikContext();
  const [{ value }, meta] = useField({
    name: props.name,
    validate: props.validate
  });

  const changeHandler = React.useCallback(
    async (asset?: ApiFieldAsset) => {
      switch (props.assetInputMode) {
        case AssetInputMode.MULTI:
          const updatedVal = [...(value || []), asset];
          await setFieldValue(props.name, updatedVal);
          break;
        default:
          await setFieldValue(props.name, asset || undefined);
      }
      if (!meta.touched) {
        await setFieldTouched(props.name, true, false);
      }
    },
    [meta.touched, props.name, setFieldTouched, setFieldValue, value, props.assetInputMode]
  );

  const onAssetRemoveHandler = React.useCallback(
    async (assetId: string) => {
      switch (props.assetInputMode) {
        case AssetInputMode.MULTI:
          const updatedVal = [...(value || [])].filter((item: ApiFieldAsset) => item.id !== assetId);
          await setFieldValue(props.name, updatedVal);
          break;
        default:
          await setFieldValue(props.name, "");
      }
      if (!meta.touched) {
        await setFieldTouched(props.name, true, false);
      }
    },
    [meta.touched, props.name, setFieldTouched, setFieldValue, value, props.assetInputMode]
  );

  const uploadedAssets: Array<ApiFieldAsset> = React.useMemo(() => {
    return props.assetInputMode === AssetInputMode.MULTI ? value || [] : [value];
  }, [value, props.assetInputMode]);

  return (
    <FormGroup className={props.className} fullWidth={props.fullWidth} indented={props.indented}>
      <FormLabel
        label={props.label}
        required={props.required}
        additionalLabel={props.additionalLabel}
        htmlFor={props.name}
        infoLabel={props.infoLabel}
      />
      {/* TODO: Handling for mult image input mode */}
      <div className="fileUploadLayout">
        <div className="fileUploadLayout_left">
          {formContextData.isReadOnly ? (
            getValueForReadOnlyFieldForStaticForm({
              renderType: FieldRenderType.IMAGE,
              value: uploadedAssets.find(asset => asset?.url)?.url
            })
          ) : (
            <React.Fragment>
              <AssetUpload
                customRenderer={props.customRenderer}
                assetConfig={props.assetConfig}
                disabled={props.disabled}
                name={props.name}
                assetType={assetType}
                registrationId={props.registrationId}
                registrationParticipantId={props.registrationParticipantId}
                assetUploadType={assetUploadType}
                onAssetUpload={changeHandler}
                uploadedAssets={uploadedAssets}
                onAssetRemove={onAssetRemoveHandler}
                hasError={Boolean(meta.touched && meta.error)}
                defaultAsset={props.defaultAsset}
                aspectRatioRestriction={props.aspectRatioRestriction}
                browseUploadCustomCopyKey={props.browseUploadCustomCopyKey}
                recommendedCustomCopy={props.recommendedCustomCopy}
                recommendedCustomCopyKey={props.recommendedCustomCopyKey}
              />
              {props.previewRenderer?.({
                disabled: props.disabled,
                uploadedAssets: uploadedAssets,
                onAssetRemove: onAssetRemoveHandler,
                dimensions: props.assetConfig?.dimension
              })}
            </React.Fragment>
          )}
          <ErrorField meta={meta} />
        </div>
        {!isEmpty(props.conflictValue) && (
          <div className="fileUploadLayout_right">
            <FormOverwriteField
              assetConfig={props.assetConfig}
              conflictType={ConflictType.IMAGE}
              forFieldName={props.name}
              disabled={props.disabled}
              customDisplayValue={props.conflictValue.url}
            />
          </div>
        )}
      </div>
      {props.enableFieldReset && <FieldResetWhenRemoved name={props.name} />}
    </FormGroup>
  );
}

export default FormAssetUploadField;
